import { useCallback, useEffect, useState } from 'react'
import { applyPrefs, DEFAULT_PREFS, type Prefs } from './themes.js'

const STORAGE_KEY = 'chess:prefs'

const readLocal = (): Prefs => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return DEFAULT_PREFS
    const parsed = JSON.parse(raw) as Partial<Prefs>
    return {
      board: typeof parsed.board === 'string' ? parsed.board : DEFAULT_PREFS.board,
      pieces: typeof parsed.pieces === 'string' ? parsed.pieces : DEFAULT_PREFS.pieces,
    }
  } catch {
    return DEFAULT_PREFS
  }
}

const writeLocal = (prefs: Prefs): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    /* Private browsing. The theme still applies for this session. */
  }
}

/**
 * Board theme and piece set, applied to the document and kept in two places.
 *
 * Local storage is the fast path and the only one a guest has; the account copy
 * is what makes the choice follow you to a phone. Local always wins on load —
 * the setting you can see is the setting that applies, and a network round trip
 * must never repaint the board out from under you a second after it appeared.
 */
export const usePrefs = (
  signedIn: boolean,
): { readonly prefs: Prefs; readonly setPrefs: (next: Prefs) => void } => {
  const [prefs, setLocal] = useState<Prefs>(readLocal)

  useEffect(() => {
    applyPrefs(document.documentElement, prefs)
  }, [prefs])

  // On first sign-in on a new device there is nothing stored server-side yet,
  // so this pushes what is here rather than pulling nothing over it.
  useEffect(() => {
    if (!signedIn) return
    let cancelled = false

    void (async (): Promise<void> => {
      try {
        const response = await fetch('/api/me/prefs', { credentials: 'same-origin' })
        if (!response.ok) return
        const body = (await response.json()) as { prefs: Partial<Prefs> | null }
        if (cancelled) return

        if (body.prefs === null) {
          await fetch('/api/me/prefs', {
            method: 'PUT',
            credentials: 'same-origin',
            body: JSON.stringify(readLocal()),
          })
          return
        }
        const merged: Prefs = {
          board: typeof body.prefs.board === 'string' ? body.prefs.board : DEFAULT_PREFS.board,
          pieces: typeof body.prefs.pieces === 'string' ? body.prefs.pieces : DEFAULT_PREFS.pieces,
        }
        writeLocal(merged)
        setLocal(merged)
      } catch {
        /* The local copy is already applied; nothing is broken. */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [signedIn])

  const setPrefs = useCallback(
    (next: Prefs): void => {
      writeLocal(next)
      setLocal(next)
      if (!signedIn) return
      void fetch('/api/me/prefs', {
        method: 'PUT',
        credentials: 'same-origin',
        body: JSON.stringify(next),
      }).catch(() => {
        /* Saved locally regardless. */
      })
    },
    [signedIn],
  )

  return { prefs, setPrefs }
}
